import { COLOR, FONT, INR, SIZE } from './tokens';
import { border, colLetter, fill } from './layout';

/**
 * Plain styled table — no Excel Table XML (mobile Excel / WPS / Google Sheets compatible).
 * Totals use SUM formulas on the data range.
 */
export function addDataTable(sheet, {
  startRow,
  startCol = 1,
  columns,
  rows,
  totalsRow = false,
}) {
  const colCount = columns.length;
  const headerRow = startRow;
  const dataStartRow = startRow + 1;
  const dataEndRow = startRow + rows.length;

  columns.forEach((col, i) => {
    const cell = sheet.getCell(headerRow, startCol + i);
    cell.value = col.name;
    cell.font = { name: FONT, size: SIZE.tableHead, bold: true, color: { argb: COLOR.white } };
    cell.fill = fill(COLOR.navy);
    cell.border = border(COLOR.navyLight);
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  });
  sheet.getRow(headerRow).height = 26;

  rows.forEach((row, ri) => {
    const r = dataStartRow + ri;
    const bg = ri % 2 === 1 ? COLOR.zebra : COLOR.white;
    row.forEach((val, ci) => {
      const cell = sheet.getCell(r, startCol + ci);
      cell.value = val;
      cell.font = { name: FONT, size: SIZE.body, color: { argb: COLOR.navy } };
      cell.fill = fill(bg);
      cell.border = border();
    });
  });

  let manualTotalsRow = null;
  if (totalsRow && rows.length > 0) {
    manualTotalsRow = writeManualTotalsRow(sheet, {
      row: dataEndRow + 1,
      startCol,
      dataStartRow,
      dataEndRow,
      columns,
    });
  }

  return {
    tableStartRow: startRow,
    tableEndRow: manualTotalsRow || dataEndRow,
    headerRow,
    dataStartRow,
    dataEndRow,
    totalsRow: manualTotalsRow,
    colCount,
    endCol: startCol + colCount - 1,
  };
}

function writeManualTotalsRow(sheet, { row, startCol, dataStartRow, dataEndRow, columns }) {
  columns.forEach((col, i) => {
    const ci = startCol + i;
    const cell = sheet.getCell(row, ci);
    const colL = colLetter(ci);
    cell.font = { name: FONT, size: SIZE.body, bold: true, color: { argb: COLOR.navy } };
    cell.fill = fill(COLOR.goldBg);
    cell.border = border();

    if (i === 0) {
      cell.value = col.totalsRowLabel || 'TOTAL';
      cell.alignment = { horizontal: 'center', vertical: 'middle', indent: 0, shrinkToFit: false };
      return;
    }

    if (col.totalsRowFunction === 'sum') {
      cell.value = { formula: `SUM(${colL}${dataStartRow}:${colL}${dataEndRow})` };
      cell.alignment = { horizontal: 'right', vertical: 'middle', shrinkToFit: false };
    } else if (col.totalsRowFunction === 'count') {
      cell.value = { formula: `COUNTA(${colL}${dataStartRow}:${colL}${dataEndRow})` };
      cell.alignment = { horizontal: 'center', vertical: 'middle', shrinkToFit: false };
    }
  });
  sheet.getRow(row).height = 28;
  return row;
}

export function currencyColumn(name, { filter = false, total = false } = {}) {
  return {
    name,
    filterButton: filter,
    totalsRowFunction: total ? 'sum' : 'none',
  };
}

export function textColumn(name, { filter = false, total = false, label = null } = {}) {
  return {
    name,
    filterButton: filter,
    totalsRowFunction: total ? 'count' : 'none',
    totalsRowLabel: label || undefined,
  };
}

export function labelTotalColumn(name, { filter = false } = {}) {
  return {
    name,
    filterButton: filter,
    totalsRowFunction: 'none',
    totalsRowLabel: 'TOTAL',
  };
}

export function sumColumn(name, { filter = false, total = false, label = null } = {}) {
  return {
    name,
    filterButton: filter,
    totalsRowFunction: total ? 'sum' : 'none',
    totalsRowLabel: label || undefined,
  };
}

export function applyTableBodyFont(sheet, startRow, endRow, startCol, endCol) {
  for (let r = startRow; r <= endRow; r += 1) {
    for (let c = startCol; c <= endCol; c += 1) {
      const cell = sheet.getCell(r, c);
      if (!cell.font?.bold) {
        cell.font = { name: FONT, size: SIZE.body, color: { argb: COLOR.navy } };
      }
    }
  }
}

export function applyInrFormatToTableColumn(sheet, colIndex, startRow, endRow, numFmt = INR) {
  for (let r = startRow; r <= endRow; r += 1) {
    const cell = sheet.getCell(r, colIndex);
    const val = cell.value;
    if (typeof val === 'number' || (val && typeof val === 'object' && val.formula)) {
      cell.numFmt = numFmt;
      cell.alignment = { horizontal: 'right', vertical: 'middle' };
    }
  }
}

export function tableColumnLetters(startCol, count) {
  return Array.from({ length: count }, (_, i) => colLetter(startCol + i));
}
